/**
 * Runs, slices and deliveries for the distribution mock: what a send produced, per recipient,
 * and what each copy holds. Split from distributionsApi.mock.ts to keep both files readable.
 */
import { andList, formatMoneyMillions, formatSignedChange, slugOf } from "./distributionFormat";
import { canonicalValue, getRecipientIssues, hasBlockingIssue, type RecipientRuleContext } from "./distributionRecipientRules";
import { excludedItemIdsOf, withTurnedOff } from "./distributionItemSwitches";
import {
  MOCK_DATA_AS_OF,
  MOCK_FIELD_BLOCKS,
  MOCK_OPS_NOTE,
  MOCK_PLAYBOOK_URL,
  MOCK_STORY_TITLE,
  MOCK_TILES,
  MOCK_TURNED_OFF_MESSAGE,
  mockStatFor,
} from "./distributions.mockData";
import type {
  CopyMessageApi,
  CopyTileApi,
  DeliveryApi,
  DistributionApi,
  FormatApi,
  LeftOutTileApi,
  RecipientInputApi,
  RunCountsApi,
  RunDetailApi,
  RunStatusApi,
  RunTriggerApi,
  SliceBrandApi,
  SliceDetailApi,
  SliceSummaryApi,
  SplitCheckApi,
  TileRefApi,
} from "./distributions.types";

/** Personalized tiles and left-out tiles for a split field (prototype `tilePlan()`). */
export const mockSplitCheck = (fieldLabel: string): SplitCheckApi => {
  const blocks = MOCK_FIELD_BLOCKS[fieldLabel] ?? {};
  const personalized: TileRefApi[] = [];
  const leftOut: LeftOutTileApi[] = [];
  MOCK_TILES.forEach((tile) => {
    const ref: TileRefApi = { item_id: tile.id, title: tile.title, kind: tile.kind, sensitive: tile.sensitive };
    if (tile.leftOut) {
      leftOut.push({ ...ref, reason: tile.leftOut.reason, message: tile.leftOut.message, can_opt_in: tile.leftOut.canOptIn });
    } else if (blocks[tile.id]) {
      leftOut.push({ ...ref, reason: blocks[tile.id].reason, message: blocks[tile.id].message, can_opt_in: false });
    } else {
      personalized.push(ref);
    }
  });
  return { total_tiles: MOCK_TILES.length, personalized, left_out: leftOut };
};

const CATEGORY_SHARE: [string, number][] = [["Grocery", 0.29], ["Fresh", 0.2], ["Beverages", 0.13], ["Home", 0.11], ["Health & beauty", 0.09]];
const STORE_SHARE = [0.082, 0.073, 0.066, 0.063, 0.061];
const STORE_DELTA = [6.0, 4.4, 3.1, 2.2, -0.8];
const STORES_BY_REGION: Record<string, string[]> = {
  West: ["Union Square", "Pearl District", "Mission Bay", "Capitol Hill", "Santa Monica"],
  East: ["Back Bay", "Tribeca", "Georgetown", "Park Slope", "Rittenhouse"],
  Central: ["Lincoln Park", "The Loop", "Short North", "Crossroads", "Uptown Minneapolis"],
  South: ["Uptown Dallas", "Midtown Atlanta", "Buckhead", "South Congress", "Wynwood"],
  International: ["Toronto Queen St", "London Soho", "Berlin Mitte", "Dublin Grafton", "Amsterdam Centrum"],
};
const WEEKS = ["W34", "W35", "W36", "W37", "W38", "W39", "W40", "W41"];

const barSpec = (x: string, y: string, xType: "ordinal" | "nominal"): Record<string, unknown> => ({
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  mark: "bar",
  encoding: { x: { field: x, type: xType }, y: { field: y, type: "quantitative" } },
});

const directionOf = (v: number) => (v > 0 ? "up" : v < 0 ? "down" : "flat") as "up" | "down" | "flat";

/** The tiles of one value's copy, in story order; sensitive values masked for everyone. */
export const mockCopyTiles = (
  fieldLabel: string,
  value: string,
  sendAsIs: readonly string[],
  excludedItemIds: readonly string[] = [],
): CopyTileApi[] => {
  const s = mockStatFor(fieldLabel, value);
  const out = new Set([...mockSplitCheck(fieldLabel).left_out.map((tile) => tile.item_id), ...excludedItemIds]);
  const stores = STORES_BY_REGION[value] ?? STORES_BY_REGION.West;
  const tiles: CopyTileApi[] = [
    { kind: "kpi", item_id: "k1", position: 1, width: 4, title: "Net sales", formatted: formatMoneyMillions(s.sales), delta_formatted: formatSignedChange(s.d, "%"), direction: directionOf(s.d), comparison_label: "vs last week", masked: false },
    { kind: "kpi", item_id: "k2", position: 2, width: 4, title: "Gross margin", formatted: null, delta_formatted: null, direction: null, comparison_label: null, masked: true },
    { kind: "kpi", item_id: "k3", position: 3, width: 4, title: "Avg basket", formatted: `$${s.bk.toFixed(2)}`, delta_formatted: formatSignedChange(s.bkd, "%"), direction: directionOf(s.bkd), comparison_label: "vs last week", masked: false },
    { kind: "chart", item_id: "c1", position: 4, width: 12, title: "Net sales by week", caption: `${value} only`, chart_type: "bar", vega_spec: barSpec("week", "net_sales", "ordinal"), rows: s.wk.map((v, i) => ({ week: WEEKS[i], net_sales: v })), masked_columns: [] },
    { kind: "chart", item_id: "c2", position: 5, width: 6, title: "Sales by category", caption: null, chart_type: "bar", vega_spec: barSpec("category", "sales", "nominal"), rows: CATEGORY_SHARE.map(([category, share]) => ({ category, sales: +(s.sales * share).toFixed(2) })), masked_columns: [] },
    { kind: "chart", item_id: "c3", position: 6, width: 6, title: "Top 5 stores", caption: null, chart_type: "bar", vega_spec: barSpec("store", "sales", "nominal"), rows: stores.map((store, i) => ({ store, sales: +(s.sales * STORE_SHARE[i]).toFixed(2) })), masked_columns: [] },
    {
      kind: "table", item_id: "t1", position: 7, width: 12, title: "Store league table",
      columns: [{ name: "store", label: "Store", masked: false }, { name: "sales", label: "Sales", masked: false }, { name: "change", label: "vs last week", masked: false }, { name: "margin", label: "Gross margin", masked: true }],
      rows: stores.map((store, i) => ({ store, sales: formatMoneyMillions(s.sales * STORE_SHARE[i]), change: formatSignedChange(STORE_DELTA[i], "%"), margin: null })),
    },
    { kind: "note", item_id: "n1", position: 8, width: 6, text: MOCK_OPS_NOTE },
    { kind: "link", item_id: "l1", position: 9, width: 6, url: MOCK_PLAYBOOK_URL, title: "Pricing playbook" },
  ];
  return tiles.filter((tile) => (tile.kind === "note" || tile.kind === "link" ? sendAsIs.includes(tile.item_id) : !out.has(tile.item_id)));
};

/** The prototype's AI bullets for one value, without the masked gross margin. */
export const mockSummary = (fieldLabel: string, value: string): string => {
  const s = mockStatFor(fieldLabel, value);
  if (s.empty) return `${value} had no sales this week.`;
  const best = s.wk[7] >= Math.max(...s.wk);
  const ups = s.wk.slice(1).filter((x, k) => x > s.wk[k]).length;
  const lines = [
    `Net sales were ${formatMoneyMillions(s.sales)}, ${s.d >= 0 ? "up" : "down"} ${Math.abs(s.d).toFixed(1)}% on last week${best ? ", the best of the last 8 weeks" : ""}.`,
  ];
  if (!MOCK_FIELD_BLOCKS[fieldLabel]?.k3) lines.push(`Average basket ${s.bkd >= 0 ? "rose" : "fell"} ${Math.abs(s.bkd).toFixed(1)}% to $${s.bk.toFixed(2)}.`);
  lines.push(`${value} sales grew in ${ups} of the last 7 weeks.`);
  return lines.join("\n");
};

export const mockFileName = (value: string, format: FormatApi): string => `${MOCK_STORY_TITLE} – ${value}.${format}`;

const fileSize = (value: string, format: FormatApi): number => {
  const seed = Array.from(value).reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0);
  return format === "pdf" ? 900_000 + (seed % 5) * 100_000 : 2_600_000 + (seed % 7) * 100_000;
};

export const mockSliceId = (runId: string, value: string): string => `${runId}-slice-${slugOf(value) || "blank"}`;

/** A recorded run plus how many times it has been read, so a live one can move along. */
export interface MockRunRecord {
  detail: RunDetailApi;
  /** Created in this session: reads move it queued → running → done. */
  live: boolean;
  polls: number;
  fieldLabel: string;
  sendAsIs: string[];
  /** Items turned off in step 1 when the run was made (round 6). */
  excludedItemIds?: string[];
  /** Round 6: each slice's composed messages and brand, by slice id (runs started in this session). */
  messages?: Record<string, CopyMessageApi[]>;
  brands?: Record<string, SliceBrandApi | null>;
}

export interface BuildRunInput {
  runId: string;
  trigger: RunTriggerApi;
  dist: DistributionApi;
  rows: RecipientInputApi[];
  ctx: RecipientRuleContext;
  includedValues: readonly string[];
  emptyValues: readonly string[];
  startedAtMs: number;
  /** Address to send a test to (the caller's own email). */
  testAddress?: string;
  /** Per-row failures to stage, keyed by position: `error` is shown verbatim. */
  failures?: Record<number, string>;
  /** Per-row holds to stage, keyed by position: a row error the run found then; `error` is shown verbatim. */
  held?: Record<number, string>;
}

export const countsOf = (deliveries: DeliveryApi[], slices: number): RunCountsApi => ({
  copies: slices,
  sent: deliveries.filter((d) => d.status === "sent").length,
  failed: deliveries.filter((d) => d.status === "failed").length,
  skipped: deliveries.filter((d) => d.status === "skipped").length,
  held: deliveries.filter((d) => d.status === "held").length,
});

/** One run: a delivery per recipient row (or the test address) and a slice per value. */
export const buildMockRun = (input: BuildRunInput): MockRunRecord => {
  const { dist, ctx, trigger } = input;
  const fieldLabel = dist.split?.label ?? "Region";
  const finishedMs = input.startedAtMs + 192_000;
  const deliveries: DeliveryApi[] = [];
  const values = new Set<string>();
  input.rows.forEach((row) => {
    const index = ctx.rows.indexOf(row);
    const issues = getRecipientIssues(row, index < 0 ? 0 : index, ctx);
    const value = canonicalValue(row.value, ctx.values) ?? row.value;
    const recipient = {
      position: row.position,
      name: row.name,
      value,
      channel: row.channel,
      address: trigger === "test" ? input.testAddress ?? row.address : row.address,
      cc: trigger === "test" ? [] : row.cc,
    };
    const base: DeliveryApi = {
      id: `${input.runId}-d${row.position}`,
      slice_id: null,
      recipient,
      formats: row.formats ?? dist.content.formats,
      status: "sent",
      error: null,
      sent_at: null,
    };
    if (trigger === "preview") return;
    if (hasBlockingIssue(issues)) {
      deliveries.push({ ...base, status: "held", error: `Held until it’s fixed: ${issues.find((i) => i.severity === "error")?.message}` });
      return;
    }
    if (input.emptyValues.includes(value)) {
      deliveries.push({ ...base, status: "skipped", error: "Skipped: no data for this value in this run." });
      return;
    }
    if (!input.includedValues.includes(value)) {
      deliveries.push({ ...base, status: "skipped", error: `Skipped: ${value} is left out in Split by.` });
      return;
    }
    // A variable whose column is blank for this row is left blank in the copy, which still sends
    // (Story Manager renders tokens with missing="blank"); it never holds the copy.
    values.add(value);
    const sliceId = mockSliceId(input.runId, value);
    const held = input.held?.[row.position];
    if (held) {
      deliveries.push({ ...base, slice_id: sliceId, status: "held", error: held });
      return;
    }
    const failure = input.failures?.[row.position];
    if (failure) {
      deliveries.push({ ...base, slice_id: sliceId, status: "failed", error: failure });
      return;
    }
    deliveries.push({ ...base, slice_id: sliceId, sent_at: new Date(input.startedAtMs + 60_000 + row.position * 9_000).toISOString() });
  });
  if (trigger === "preview") input.rows.forEach((row) => values.add(canonicalValue(row.value, ctx.values) ?? row.value));

  const excludedItemIds = excludedItemIdsOf(dist.content);
  const leftOut = withTurnedOff(mockSplitCheck(fieldLabel), excludedItemIds, MOCK_TURNED_OFF_MESSAGE).left_out.filter(
    (tile) => !dist.send_unfiltered_item_ids.includes(tile.item_id),
  );
  const slices: SliceSummaryApi[] = Array.from(values).map((value) => {
    const formats = Array.from(new Set(input.rows.filter((r) => (canonicalValue(r.value, ctx.values) ?? r.value) === value).flatMap((r) => r.formats ?? dist.content.formats)));
    return {
      id: mockSliceId(input.runId, value),
      value,
      status: trigger === "preview" ? "built" : "rendered",
      template_id: dist.content.template_id,
      theme_id: dist.content.theme_id,
      files: formats.map((format) => ({ format, filename: mockFileName(value, format), bytes: fileSize(value, format) })),
      left_out: leftOut,
      error: null,
    };
  });
  const counts = countsOf(deliveries, slices.length);
  const status: RunStatusApi = counts.failed ? (counts.sent ? "partly_failed" : "failed") : "succeeded";
  return {
    live: false,
    polls: 0,
    fieldLabel,
    sendAsIs: dist.send_unfiltered_item_ids,
    excludedItemIds,
    detail: {
      run: {
        id: input.runId,
        trigger,
        status,
        scheduled_for: trigger === "schedule" ? new Date(input.startedAtMs).toISOString() : null,
        queued_at: new Date(input.startedAtMs - 4_000).toISOString(),
        started_at: new Date(input.startedAtMs).toISOString(),
        finished_at: new Date(finishedMs).toISOString(),
        counts,
        requested_by: trigger === "schedule" ? null : { id: "user-hardik", name: "Hardik Chheda", email: null },
        error: counts.failed ? `${counts.failed} ${counts.failed === 1 ? "copy" : "copies"} didn’t arrive: ${andList(deliveries.filter((d) => d.status === "failed").map((d) => d.recipient.address))}.` : null,
        data_as_of: MOCK_DATA_AS_OF,
      },
      slices,
      deliveries,
    },
  };
};

/** A live run as the n-th read sees it: queued, then running, then its final state. */
export const projectMockRun = (record: MockRunRecord): RunDetailApi => {
  const detail: RunDetailApi = JSON.parse(JSON.stringify(record.detail));
  if (!record.live || record.polls >= 3) return detail;
  if (record.polls <= 1) {
    detail.run.status = "queued";
    detail.run.started_at = null;
    detail.run.finished_at = null;
    detail.run.error = null;
    detail.run.counts = { copies: 0, sent: 0, failed: 0, skipped: 0, held: 0 };
    detail.slices = detail.slices.map((slice) => ({ ...slice, status: "pending", files: [] }));
    detail.deliveries = detail.deliveries.map((d) => ({ ...d, status: "pending", error: null, sent_at: null }));
    return detail;
  }
  const half = Math.ceil(detail.deliveries.length / 2);
  detail.run.status = "running";
  detail.run.finished_at = null;
  detail.run.error = null;
  detail.slices = detail.slices.map((slice, i) => (i < Math.ceil(detail.slices.length / 2) ? slice : { ...slice, status: "rendering", files: [] }));
  detail.deliveries = detail.deliveries.map((d, i) => (i < half ? d : { ...d, status: "sending", error: null, sent_at: null }));
  detail.run.counts = countsOf(detail.deliveries.slice(0, half), Math.ceil(detail.slices.length / 2));
  return detail;
};

/** A run frozen mid-send: the first half delivered, the rest sending now. Never advances on reads. */
export const freezeMockRunSending = (record: MockRunRecord): MockRunRecord => ({
  ...record,
  detail: projectMockRun({ ...record, live: true, polls: 2 }),
  live: false,
});

/** GET slice: the copy's tiles and its AI summary. */
export const mockSliceDetail = (record: MockRunRecord, sliceId: string): SliceDetailApi | null => {
  const slice = record.detail.slices.find((s) => s.id === sliceId);
  if (!slice) return null;
  return {
    slice: JSON.parse(JSON.stringify(slice)),
    tiles: mockCopyTiles(record.fieldLabel, slice.value, record.sendAsIs, record.excludedItemIds),
    summary: mockSummary(record.fieldLabel, slice.value),
    data_as_of: MOCK_DATA_AS_OF,
    messages: record.messages?.[sliceId] ?? [],
    brand: record.brands?.[sliceId] ?? null,
  };
};
